<template>
  <rpm-layout>
    <div class="bg-white rounded-lg p-2">
      <p class="text-gray-700 text-2xl font-bold mb-2">
        Outcome Report
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on">mdi-help-circle</v-icon>
          </template>
          <span>
            Report shows outcome data for a client.<br/>
          </span>
        </v-tooltip>
      </p>
      <div class="flex justify-between items-center p-2">
        <div>
          <el-button :disabled="loading" type="default" @click="getData">Refresh</el-button>
        </div>
      </div>
      <div class="flex gap-2 items-center mb-2">
        <v-select
            v-model="client"
            :items="availableClients.map(client => ({ value: client.id, text: client.name }))"
            label="Client"
            placeholder="Select Client"
            filterable
        ></v-select>
      </div>

      <v-card v-if="reportData" outlined class="mb-4 pa-4">
        <div class="mb-4">
          <div><strong>{{ fieldLabels.usersTotal }}:</strong> {{ reportData.usersTotal || 'N/A' }}</div>
          <div><strong>{{ fieldLabels.usersActiveRpmTotal }}:</strong> {{ reportData.usersActiveRpmTotal || 'N/A' }}</div>
          <div><strong>{{ fieldLabels.usersActiveCcmTotal }}:</strong> {{ reportData.usersActiveCcmTotal || 'N/A' }}</div>
          <div><strong>{{ fieldLabels.usersActiveApcmTotal }}:</strong> {{ reportData.usersActiveApcmTotal || 'N/A' }}</div>
        </div>

        <div class="mb-4">
          <div><strong>Age Ranges:</strong></div>
          <v-simple-table dense v-if="reportData.ageRanges && Object.keys(reportData.ageRanges).length">
            <tbody>
            <tr v-for="(count, range) in reportData.ageRanges" :key="range">
              <td>{{ range }}</td>
              <td>{{ count }}</td>
            </tr>
            </tbody>
          </v-simple-table>
          <div v-else>No age range data available.</div>
        </div>

        <div class="mb-4">
          <div><strong>Sex:</strong></div>
          <v-simple-table dense v-if="reportData.sex">
            <tbody>
            <tr>
              <td>{{ fieldLabels.m }}</td>
              <td>{{ reportData.sex.m || 'N/A' }}</td>
            </tr>
            <tr>
              <td>{{ fieldLabels.f }}</td>
              <td>{{ reportData.sex.f || 'N/A' }}</td>
            </tr>
            <tr>
              <td>{{ fieldLabels.unknown }}</td>
              <td>{{ reportData.sex.unknown || 'N/A' }}</td>
            </tr>
            </tbody>
          </v-simple-table>
          <div v-else>No sex data available.</div>
        </div>

        <div class="mb-4">
          <div><strong>Care Plan Durations:</strong></div>
          <v-simple-table dense v-if="reportData.carePlanDurations && Object.keys(reportData.carePlanDurations).length">
            <thead>
            <tr>
              <th>Type</th>
              <th>{{ fieldLabels.usersWithData }}</th>
              <th>{{ fieldLabels.totalDays }}</th>
              <th>{{ fieldLabels.averageDays }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(plan, type) in reportData.carePlanDurations" :key="type">
              <td>{{ type.toUpperCase() }}</td>
              <td>{{ plan.usersWithData || 'N/A' }}</td>
              <td>{{ plan.totalDays !== undefined ? plan.totalDays.toFixed(1) : 'N/A' }}</td>
              <td>{{ plan.averageDays !== undefined ? plan.averageDays.toFixed(1) : 'N/A' }}</td>
            </tr>
            </tbody>
          </v-simple-table>
          <div v-else>No care plan duration data available.</div>
        </div>

        <div class="mb-4">
          <div><strong>Touchpoints:</strong></div>
          <div v-if="reportData.touchpoints">
            {{ fieldLabels.deviceSyncs }}: {{ reportData.touchpoints.deviceSyncs || 'N/A' }}<br>
            {{ fieldLabels.deviceSyncsAverage }}: {{ reportData.touchpoints.deviceSyncsAverage || 'N/A' }}
          </div>
          <div v-else>No touchpoint data available.</div>
        </div>

        <div class="mb-4">
          <div class="mt-2"><strong>Tasks:</strong></div>
          <v-simple-table dense v-if="reportData.tasks && Object.keys(reportData.tasks).length">
            <thead>
            <tr>
              <th>Type</th>
              <th>{{ fieldLabels.totalMinutes }}</th>
              <th>{{ fieldLabels.totalMinutesAverage }}</th>
              <th>{{ fieldLabels.minutesPerMonthAverage }}</th>
              <th>{{ fieldLabels.totalMonths }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(task, type) in reportData.tasks" :key="type">
              <td>{{ type.toUpperCase() }}</td>
              <td>{{ task.totalMinutes || 'N/A' }}</td>
              <td>{{ task.totalMinutesAverage || 'N/A' }}</td>
              <td>{{ task.minutesPerMonthAverage || 'N/A' }}</td>
              <td>{{ task.totalMonths || 'N/A' }}</td>
            </tr>
            </tbody>
          </v-simple-table>
          <div v-else>No task data available.</div>
        </div>

        <div class="mb-4">
          <div><strong>Metrics:</strong></div>
          <v-simple-table dense v-if="reportData.metrics && firstMetricKeys.length">
            <thead>
            <tr>
              <th>Type</th>
              <th v-for="key in firstMetricKeys" :key="key">{{ fieldLabels[key] || key }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(metric, type) in reportData.metrics" :key="type">
              <td>{{ type }}</td>
              <td v-for="key in firstMetricKeys" :key="key">{{ metric[key] || 'N/A' }}</td>
            </tr>
            </tbody>
          </v-simple-table>
          <div v-else>No metrics data available.</div>
        </div>
      </v-card>

      <div v-if="downloadReportPatient">
        <patient-report :patient="downloadReportPatient" :category="category" v-on:downloaded="downloadReport(null)"/>
      </div>
    </div>
  </rpm-layout>
</template>

<style>
body {
  margin: 0;
}
</style>

<script>
import RpmLayout from '../../layouts/RpmLayout.vue';
import PatientReport from './PatientReport.vue';
import moment from "moment-timezone";
import Vue from "vue";
import PatientMixin from "@/mixins/Patient";

export default {
  mixins: [PatientMixin],
  components: {RpmLayout, PatientReport},
  data() {
    return {
      client: null,
      date_from: moment().format('YYYY-MM'),
      date_to: moment().format('YYYY-MM'),
      reportData: [],
      token: null,
      loading: true,
      downloadReportPatient: null,
    };
  },
  created: async function () {
    this.token = await this.$auth.getTokenSilently();
    Promise.all([
      !this.$store.state.patients.authUser.id ? this.$store.dispatch('getAuthUser') : null,
      !this.$store.state.patients.patients.length ? this.$store.dispatch('getPatients') : null,
      !this.$store.state.patients.providers.length ? this.$store.dispatch('getProviders') : null,
    ]).finally(async () => {
      this.client = this.availableClients[0].id;
      await this.getData();
      this.loading = false;
    });
  },
  mounted: function () {
  },
  computed: {
    firstMetricKeys() {
      if (this.reportData && this.reportData.metrics) {
        const first = Object.values(this.reportData.metrics)[0];
        return first ? Object.keys(first) : [];
      }
      return [];
    },
    clientName() {
      if (!this.client) return '';
      return this.availableClients.find((c) => c.id == this.client).name;
    },
    availableClients() {
      let available_clients = this.$store.state.patients.authUser.available_clients;
      return available_clients && available_clients.length ? available_clients : [];
    },
    objectToCSV() {
      if (!this.tableRows.length) {
        return '';
      }
      const escapeField = field => `"${(''+field).replace(/"/g, '""').replace(/\n/g, '\\n')}"`;
      const stripHTML = field => field.replace(/<\/?[^>]+(>|$)/g, "");
      const csvHeaders = this.tableHeaders.map(header => escapeField(header.text)).join(',');

      const csvRows = this.tableRows.map(row => {
        return this.tableHeaders.map((header, index) => {
          if (index === 0) {
            return escapeField(row[header.value] || '');
          } else {
            return escapeField(stripHTML(row[header.value] || '0'));
          }
        }).join(',');
      }).join('\n');

      return `${csvHeaders}\n${csvRows}`;
    },
    fieldLabels() {
      return {
        // Top-level user counts
        usersTotal: 'Total Users',
        usersActiveRpmTotal: 'Active RPM Users',
        usersActiveCcmTotal: 'Active CCM Users',
        usersActiveApcmTotal: 'Active APCM Users',

        // Age ranges (labels are already readable, except 'unknown')
        unknown: 'Unknown',

        // Sex
        m: 'Male',
        f: 'Female',

        // Touchpoints
        deviceSyncs: 'Device Syncs',
        deviceSyncsAverage: 'Average Device Syncs',

        // Care Plan Durations
        usersWithData: 'Users With Data',
        totalDays: 'Total Days',
        averageDays: 'Average Days',

        // Tasks
        totalMinutes: 'Total Minutes',
        totalMinutesAverage: 'Average Minutes',
        minutesPerMonthAverage: 'Average Minutes/Month',
        totalMonths: 'Total Months',

        // Care plan types
        rpm: 'RPM',
        ccm: 'CCM',
        apcm: 'APCM',

        // Metrics
        bloodPressure: 'Blood Pressure',
        weight: 'Weight',
        bloodGlucose: 'Blood Glucose',

        averageInitialSystolic: 'Avg. Initial Systolic',
        averageLatestSystolic: 'Avg. Latest Systolic',
        averageSystolicReduction: 'Avg. Systolic Reduction',
        averageInitialDiastolic: 'Avg. Initial Diastolic',
        averageLatestDiastolic: 'Avg. Latest Diastolic',
        averageDiastolicReduction: 'Avg. Diastolic Reduction',

        averageInitialWeight: 'Avg. Initial Weight',
        averageLatestWeight: 'Avg. Latest Weight',
        averageWeightReduction: 'Avg. Weight Reduction',

        averageInitialGlucose: 'Avg. Initial Glucose',
        averageLatestGlucose: 'Avg. Latest Glucose',
        averageGlucoseReduction: 'Avg. Glucose Reduction',
      };
    },
  },
  watch: {
    date_from() {
      this.getData();
    },
    date_to() {
      this.getData();
    },
    client() {
      this.getData();
    },
  },
  methods: {
    csvLink() {
      const file = new File([this.objectToCSV], 'task-report.csv', {type: 'text/csv',});
      return URL.createObjectURL(file);
    },
    async getData() {
      if (!this.client || !this.date_from || !this.date_to) {
        return;
      }

      if (this.date_to < this.date_from) {
        this.$awn.warning('Date from/to need to be in valid ranges');
        return;
      }

      this.loading = true;
      return Vue.$http
          .get("/api/rpm_ccm_dashboard/outcome-report-data", {
            params: {
              token: await this.token,
              client: this.client,
            },
          })
          .then((res) => {
            this.reportData = res.data.data;
          })
          .catch((error) => this.$awn.warning(error.response.exception))
          .finally(() => {
            this.loading = false;
          });
    },
  },
};
</script>

<style scoped>
.freeze {
  position: sticky;
  left: 0;
  background: white;
  z-index: 1;
}
</style>